<template>
    <div class="card p-4">
        <ConfirmDialog></ConfirmDialog>
        <Toast></Toast>
        <!-- Cabecera de la página con PrimeFlex -->
        <div class="flex justify-content-between align-items-center mb-4">
            <h2 class="m-0">Gestión de Recetas</h2>
            <Button 
                v-if="'Receta Crear' in permisosUsuarioAuth"
                label="Nueva Receta" 
                icon="pi pi-plus" 
                class="p-button-success" 
                @click="abrirModalCrear" 
            />
        </div>

        <!-- Consumiendo el componente reutilizable CustomDataTable -->
        <CustomDataTable
            :datos="recetas"
            :columnas="columnas"
            :cargando="cargando"
            nombreEntidad="Recetas"
            dataKey="id"
            permitirExpansion
            v-model:expandedRows="filasExpandidas"
        >
            <!-- Slot dinámico para 'producto' (Accede a la relación anidada) -->
            <template #producto="{ data }">
                <span class="font-bold">{{ data.producto?.descripcion || 'Sin producto asignado' }}</span>
            </template>

            <!-- Slot dinámico para 'costo_teorico' (Formato de moneda) -->
            <template #costo_teorico="{ data }">
                Bs. {{ data.costo_teorico ? parseFloat(data.costo_teorico).toFixed(2) : '0.00' }}
            </template>

            <!-- Slot dinámico para 'estado' (Tag visual) -->
            <template #estado="{ data }">
                <span :class="'product-badge ' + (data.estado ? 'status-instock' : 'status-outofstock')">
                    {{ data.estado ? 'Activo' : 'Inactivo' }}
                </span>
            </template>

            <!-- Slot para la columna de acciones (Viene definido en CustomDataTable) -->
            <template #acciones="{ data }">
                <Button 
                    icon="pi pi-pencil" 
                    class="p-button-rounded p-button-text p-button-info mr-2" 
                    v-tooltip="'Editar'" 
                    :disabled="!('Receta Editar' in permisosUsuarioAuth)"
                    @click="irAEditar(data)" 
                />
                <span 
                    v-tooltip.top="(data.estado == 1 || data.estado == true) ? 'Desactive la receta para poder eliminarla' : (!('Receta Eliminar' in permisosUsuarioAuth) ? 'No tiene permiso para eliminar' : 'Eliminar')"
                    style="display: inline-block;"
                    :style="(data.estado == 1 || data.estado == true || !('Receta Eliminar' in permisosUsuarioAuth)) ? 'cursor: not-allowed;' : ''"
                >
                    <Button 
                        icon="pi pi-trash" 
                        class="p-button-rounded p-button-text p-button-danger" 
                        :disabled="data.estado == 1 || data.estado == true || !('Receta Eliminar' in permisosUsuarioAuth)"
                        @click="confirmarEliminacion(data)" 
                        :style="(data.estado == 1 || data.estado == true || !('Receta Eliminar' in permisosUsuarioAuth)) ? 'pointer-events: none;' : ''"
                    />
                </span>
            </template>

            <!-- Row Expansion (Detalles de la Receta) -->
            <template #expansion="{ data }">
                <div class="p-3 surface-300 border-round">
                    <h5 class="mb-3 mt-0">Ingredientes / Insumos para <b>{{ data.producto?.descripcion }}</b></h5>
                    <DataTable :value="data.detalles" responsiveLayout="scroll" class="p-datatable-sm">
                        <Column field="producto.descripcion" header="Insumo"></Column>
                        <Column field="cantidad_gramo" header="Cant. (Gramos)">
                            <template #body="{ data: detalle }">
                                {{ detalle.cantidad_gramo ? detalle.cantidad_gramo + ' g' : '-' }}
                            </template>
                        </Column>
                        <Column field="cantidad_unidad" header="Cant. (Unidades)">
                            <template #body="{ data: detalle }">
                                {{ detalle.cantidad_unidad ? detalle.cantidad_unidad + ' un' : '-' }}
                            </template>
                        </Column>
                        <Column header="Merma">
                            <template #body="{ data: detalle }">
                                <span v-if="detalle.aplica_merma">{{ detalle.merma_porcentaje }}%</span>
                                <span v-else>No Aplica</span>
                            </template>
                        </Column>
                    </DataTable>
                </div>
            </template>
        </CustomDataTable>

        <!-- Modal de Crear/Editar Receta -->
        <RecetaCreate 
            v-model:visible="mostrarModalCrear" 
            :receta="recetaSeleccionada"
            @guardado="cargarRecetas" 
        />
    </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { useConfirm } from 'primevue/useconfirm';
import { useToast } from 'primevue/usetoast';
import { useAuth } from '@/stores/auth';
// Asume que tienes el servicio configurado
import RecetaService from '@/service/RecetaService'; 
import CustomDataTable from '@/components/shared/CustomDataTable.vue'; 
import RecetaCreate from './RecetaCreate.vue';

const auth = useAuth();
const permisosUsuarioAuth = auth.user.permissions;

const recetaService = new RecetaService();
const confirm = useConfirm();
const toast = useToast();

// Variables reactivas para el estado de la tabla
const recetas = ref([]);
const cargando = ref(false);
const mostrarModalCrear = ref(false);
const recetaSeleccionada = ref(null);
const filasExpandidas = ref([]);

// Configuración de columnas que inyectamos en CustomDataTable
const columnas = ref([
    { field: 'id', header: 'ID', sortable: true },
    { field: 'producto', header: 'PRODUCTO', sortable: true },
    { field: 'costo_teorico', header: 'COSTO TEÓRICO', sortable: true },
    { field: 'estado', header: 'ESTADO', sortable: false }
]);

/**
 * Carga las recetas consumiendo el backend de Laravel
 */
const cargarRecetas = async () => {
    cargando.value = true;
    try {
        const respuesta = await recetaService.obtenerRecetas();
        console.log("Respuesta del servidor (Recetas):", respuesta);
        
        // Dependiendo de cómo devuelva Laravel (Collection, Resource, Paginator)
        if (respuesta.data && respuesta.data.data) {
            recetas.value = respuesta.data.data; 
        } else if (respuesta.data) {
            recetas.value = respuesta.data;
        } else {
            recetas.value = respuesta;
        }
    } catch (error) {
        console.error("Error al cargar las recetas:", error);
    } finally {
        cargando.value = false;
    }
};

// Rutas de navegación

const abrirModalCrear = () => {
    recetaSeleccionada.value = null;
    mostrarModalCrear.value = true;
};

const irAEditar = (receta) => {
    recetaSeleccionada.value = receta;
    mostrarModalCrear.value = true;
};

// Lógica para eliminar
const confirmarEliminacion = (receta) => {
    if (receta.estado == 1 || receta.estado == true) {
        toast.add({ severity: 'warn', summary: 'Advertencia', detail: 'No se puede eliminar una receta activa. Desactívela primero.', life: 4000 });
        return;
    }

    confirm.require({
        message: '¿Está seguro que desea eliminar la receta?',
        header: 'Confirmar Eliminación',
        icon: 'pi pi-exclamation-triangle',
        accept: async () => {
            try {
                await recetaService.eliminarReceta(receta.id);
                toast.add({ severity: 'success', summary: 'Éxito', detail: 'Receta eliminada correctamente', life: 3000 });
                cargarRecetas();
            } catch (error) {
                toast.add({ severity: 'error', summary: 'Error', detail: 'Ocurrió un error al eliminar la receta', life: 3000 });
                console.error("Error al eliminar:", error);
            }
        },
        reject: () => {
            // Cancelado
        }
    });
};

// Cargar la data automáticamente al entrar a la vista
onMounted(() => {
    cargarRecetas();
});
</script>

<style scoped lang="scss">
@import "@/assets/demo/badges.scss";
</style>